import { Box, Button, ButtonGroup, Dialog, DialogContent, MenuItem, TextField } from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers-pro'
import { BoxFC, BoxFR } from 'components/BoxCustom'
import DataGridCellExpand from 'components/DataGridCellExpand/DataGridCellExpand'
import PaperComponent from 'components/PaperComponent'
import { DialogFooter, DialogHeader } from 'components/dialog/DialogHeader'
import DateTimeSelect from 'components/DateTimeSelect'
import ComboBox from 'components/ComboBox'
import SearchButton from 'components/buttons/SearchButton'
import ClearButton from 'components/buttons/ClearButton'
import { GlobalStateContext } from 'contexts/GlobalStateContext'
import { alertConfirm, alertConfirmDelete, alertError, alertWarning } from 'components/Alert'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { amber, green, grey, red } from '@mui/material/colors'
import { flatColumns } from './flatColumns'
import { addSumRow, sxSumColorClass } from 'utils/highlightSumColor'
import { jobOrderColNm } from 'constants/jobOrderConstant'
import { AddCircleRounded, CloudDownloadRounded, DeleteForeverRounded } from 'resource/muiImport'
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded'
import DailyJobDialogV3 from 'pages/Menu1/DailyJob/DailyJobDialogV3'

// Sts3Only: null on this branch — every coverage leg (รับ 1 / ส่ง 3 / คืน 5) is offered and
// paid separately. Part of every request, so it survives the clear button too.
const clearFilter = () => ({
  Sts3Only: null,
  DrvId: null, JobId: null, JobOrdId: null,
  JobNo: null, ContNo: null,
  StsTmSt: null,
  StsTmEn: null,
  AptTmSt: null,
  AptTmEn: null,
  Settle: null,
  Cmpt: null,
  PayTm: null,
})

const initFilter = { ...clearFilter(), Settle: 2, Cmpt: 1 }

const DAY_MS = 24 * 60 * 60 * 1000

// Board ordering + grouping rule:
//  1. order by StsTm — every leg sits at its own วันที่วิ่งงาน position.
//  2. legs of the SAME JobOrdId+driver whose StsTm are within 24h (chained) form ONE group
//     (a block with a single header + zebra shade); a >24h jump splits into a new group that is
//     positioned by its OWN StsTm. So one JobOrdId can appear in several places (different
//     drivers, or the same driver's legs that are days apart).
// Grouping needs proximity across rows, so it's done here rather than in SQL — this
// deliberately re-orders what getTripPaymentBoard returned.
export const withGroups = (rows) => {
  const ms = (r) => (r.StsTm ? dayjs(r.StsTm).valueOf() : null)

  // bucket rows by JobOrdId+driver
  const byKey = new Map()
  for (const r of rows || []) {
    const k = `${r.JobOrdId}-${r.DrvId}`
    if (!byKey.has(k)) byKey.set(k, [])
    byKey.get(k).push(r)
  }

  // within each bucket, sort by StsTm (nulls last) then ExpId, and split into 24h clusters
  const clusters = []
  for (const rs of byKey.values()) {
    rs.sort((a, b) => {
      const ta = ms(a), tb = ms(b)
      if (ta == null || tb == null) return (ta == null) - (tb == null) || (a.ExpId || 0) - (b.ExpId || 0)
      return ta - tb || (a.ExpId || 0) - (b.ExpId || 0)
    })
    let cur = null, prevTs = null
    for (const r of rs) {
      const ts = ms(r)
      const jump = cur && ts != null && prevTs != null && ts - prevTs > DAY_MS
      if (!cur || jump) {
        cur = { anchor: ts, JobOrdId: r.JobOrdId, DrvId: r.DrvId, rows: [] }
        clusters.push(cur)
      }
      cur.rows.push(r)
      if (ts != null) prevTs = ts             // null rows (extras) attach to the current cluster
    }
  }

  // order clusters by anchor StsTm (nulls last), tie-break by JobOrdId then DrvId
  clusters.sort((a, b) => {
    if (a.anchor == null || b.anchor == null) return (a.anchor == null) - (b.anchor == null) || a.JobOrdId - b.JobOrdId || a.DrvId - b.DrvId
    return a.anchor - b.anchor || a.JobOrdId - b.JobOrdId || a.DrvId - b.DrvId
  })

  // flatten: first row of each cluster is the group start; zebra alternates per cluster
  const out = []
  clusters.forEach((c, idx) => {
    c.rows.forEach((r, i) => out.push({ ...r, isGroupStart: i === 0, groupParity: idx % 2 }))
  })
  return out
}

let selectedJobOrdId
const TripPaymentBoard = () => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [filterData, setFilterData] = useState(initFilter)
  const [selectionModel, setSelectionModel] = useState([])
  const [addExpDlg, setAddExpDlg] = useState({ open: false, ExpId: null, Amnt: "" })
  const [moveDlg, setMoveDlg] = useState({ open: false, row: null, month: null })
  const [payTmDlg, setPayTmDlg] = useState({ open: false, month: null })
  const [dailyJobDialogOpen, setDailyJobDialogOpen] = useState(false)

  const getData = useCallback((filter) => {
    ax.post("/branch/driver-payroll-v3/getTripPaymentBoard", filter).then(value => {
      if (value.data) setDataTable(withGroups(value.data))
    })
  }, [ax])

  // Nothing entered here picks a งวด: a wage calculated or typed on this board is written as its
  // own ค้างจ่าย document (no SalDte sent). The งวด is set afterwards in the งวดที่จ่าย column, and
  // the payroll claims it. docs/driver-payroll-v3-accrual-plan.md §3.1

  // this board pays พนักงานขับรถ only — driverOnlyCombo is already IsSub = 0, so drop รถร่วมประจำ
  // (IsMng = 1), who are paid on board 8.1.2
  const drvCombo = useMemo(
    () => (msData.driverOnlyCombo || []).filter(item => msData.driverObj?.[item.id]?.IsMng !== 1),
    [msData])

  // pinned sum row (id 0) totalling the จำนวน column, styled via utils/highlightSumColor
  const modDataTable = useMemo(() => addSumRow({
    dataTable, sumColNms: ["Amnt"], sumColTotal: "ExpNm",
  }), [dataTable])

  const runCalc = useCallback((pairs) => {
    ax.post("/branch/driver-payroll-v3/calcTripWagePairs", { pairs, ...filterData }).then(value => {
      if (value.data) {
        setDataTable(withGroups(value.data.board))
        setSelectionModel([])
        const w = value.data.warnings || []
        if (w.length) alertWarning(`คำนวณ ${pairs.length} แถว — มีข้อความเตือน:\n${w.map(x => x.message).join("\n")}`)
      }
    })
  }, [ax, filterData])

  // recalc a leg row: recompute ONLY the clicked leg (not every leg of the JobOrdId+DrvId).
  // Extras/deductions carry no ContStsId and a locked payroll cannot change — neither should
  // reach runCalc (the sum row has no ContStsId either, so it is covered by the same guard).
  const handleRecalcRow = useCallback((row) => {
    if (!row.ContStsId || row.IsLocked) return
    runCalc([{ JobOrdId: row.JobOrdId, DrvId: row.DrvId, StsIds: [row.ContStsId] }])
  }, [runCalc])

  const handleCalcSelected = useCallback(() => {
    const picked = dataTable.filter(r => selectionModel.includes(r.id) && r.ContStsId && !r.IsLocked)
    if (picked.length === 0) { alertError("ไม่มีขาค่าเที่ยวที่เลือก (หรือถูกล็อค)"); return }
    // group the selected legs back into one calc pair per (JobOrdId, DrvId)
    const byJo = new Map()
    picked.forEach(r => {
      const k = `${r.JobOrdId}-${r.DrvId}`
      if (!byJo.has(k)) byJo.set(k, { JobOrdId: r.JobOrdId, DrvId: r.DrvId, StsIds: [] })
      byJo.get(k).StsIds.push(r.ContStsId)
    })
    runCalc([...byJo.values()])
  }, [dataTable, selectionModel, runCalc])

  const handleDeleteSelected = useCallback(() => {
    const picked = dataTable.filter(r => selectionModel.includes(r.id) && r.WgItmId && !r.IsLocked)
    if (picked.length === 0) { alertError("ไม่มีรายการที่ลบได้ (เลือกแถวที่มีค่าเที่ยว และไม่ถูกล็อค)"); return }
    alertConfirmDelete(() => {
      ax.post("/branch/driver-payroll-v3/deleteWageItemsBulk", {
        WgItmIds: picked.map(r => r.WgItmId), ...filterData,
      }).then(value => {
        if (value.data) { setDataTable(withGroups(value.data.board)); setSelectionModel([]) }
      })
    })
  }, [ax, dataTable, selectionModel, filterData])

  const handleDeleteRow = useCallback((row) => {
    if (!row.WgItmId) return
    alertConfirmDelete(() => {
      ax.post("/branch/driver-payroll-v3/deleteWageItemsBulk", { WgItmIds: [row.WgItmId], ...filterData }).then(value => {
        if (value.data) setDataTable(withGroups(value.data.board))
      })
    })
  }, [ax, filterData])

  const handleOpenAddExp = useCallback(() => {
    const picked = dataTable.filter(r => selectionModel.includes(r.id) && !r.IsLocked)
    if (picked.length === 0) { alertError("ไม่มีแถวที่เลือก (หรือแถวที่เลือกถูกล็อค)"); return }
    setAddExpDlg({ open: true, ExpId: null, Amnt: "" })
  }, [dataTable, selectionModel])

  const handleAddExp = useCallback(() => {
    if (!addExpDlg.ExpId || addExpDlg.Amnt === "") { alertError("กรุณาระบุรายการและจำนวนเงิน"); return }
    const picked = dataTable.filter(r => selectionModel.includes(r.id) && !r.IsLocked)
    // one line per job order (dedupe duplicate JobOrdId)
    const pairs = [...new Map(picked.map(r => [r.JobOrdId, { JobOrdId: r.JobOrdId, DrvId: r.DrvId }])).values()]
    alertConfirm(`เพิ่มรายการนี้ให้ ${pairs.length} ใบงาน?`, () => {
      ax.post("/branch/driver-payroll-v3/addExpenseBulk", {
        ...filterData, pairs, ExpId: addExpDlg.ExpId, Amnt: +addExpDlg.Amnt,
      }).then(value => {
        if (value.data) {
          setDataTable(withGroups(value.data.board)); setSelectionModel([])
          setAddExpDlg({ open: false, ExpId: null, Amnt: "" })
        }
      })
    })
  }, [ax, dataTable, selectionModel, filterData, addExpDlg])

  // edit the Amnt cell: existing line updates in place (keeps its งวด); a new leg becomes a
  // ค้างจ่าย line with no งวด. Clearing the cell deletes the line; 0 does NOT —
  // it is kept as a 0 line meaning "ขานี้ไม่มีค่าเที่ยว", so calc leaves the leg alone.
  const handleCellEditCommit = useCallback((params) => {
    if (params.field !== "Amnt") return
    const row = dataTable.find(r => r.id === params.id)
    if (!row || row.IsLocked) return
    const amnt = params.value === "" ? null : params.value
    if (row.WgItmId) {
      if (amnt == null) {
        ax.post("/branch/driver-payroll-v3/deleteWageItemsBulk", { WgItmIds: [row.WgItmId], ...filterData })
          .then(value => { if (value.data) setDataTable(withGroups(value.data.board)) })
      } else {
        ax.post("/branch/driver-payroll-v3/updateWageItem", { WgItmId: row.WgItmId, Amnt: +amnt, Dscp: row.Dscp ?? null })
          .then(value => { if (value.data) getData(filterData) })
      }
    } else {
      ax.post("/branch/driver-payroll-v3/saveTripPaymentCell", {
        JobOrdId: row.JobOrdId, DrvId: row.DrvId, ExpId: row.ExpId, Amnt: amnt, Dscp: null,
      }, false).then(value => { if (value && value.data != null) getData(filterData) })
    }
  }, [ax, dataTable, filterData, getData])

  // ตั้งงวดที่จ่าย for every selected row at once — only rows that actually carry a wage line
  // can move (an empty leg has no งวด), and a locked or already-claimed line is left alone.
  const payTmRows = useMemo(
    () => dataTable.filter(r => selectionModel.includes(r.id) && r.WgItmId && !r.IsLocked && !r.IsClaimed),
    [dataTable, selectionModel])

  const handleOpenSetPayTm = useCallback(() => {
    if (payTmRows.length === 0) { alertError("ไม่มีรายการที่ตั้งงวดได้ (เลือกแถวที่มีค่าเที่ยว และไม่ถูกล็อค/ดึงเข้างวดแล้ว)"); return }
    setPayTmDlg({ open: true, month: dayjs().startOf("month") })
  }, [payTmRows])

  const handleSetPayTm = useCallback(() => {
    if (!payTmDlg.month) { alertError("กรุณาระบุงวดที่จ่าย"); return }
    alertConfirm(`ตั้งงวดที่จ่าย ${dayjs(payTmDlg.month).format("MM/YYYY")} ให้ ${payTmRows.length} รายการ?`, () => {
      ax.post("/branch/driver-payroll-v3/moveWageItemMonthBulk", {
        ...filterData,
        WgItmIds: payTmRows.map(r => r.WgItmId),
        ToSalDte: dayjs(payTmDlg.month).format("YYYY-MM-DD"),
      }).then(value => {
        if (value.data) {
          setDataTable(withGroups(value.data.board)); setSelectionModel([])
          setPayTmDlg({ open: false, month: null })
          const w = value.data.warnings || []
          if (w.length) alertWarning(`ตั้งงวดสำเร็จ ${value.data.moved} รายการ — ข้าม ${w.length} รายการ:\n${w.map(x => x.message).join("\n")}`)
        }
      })
    })
  }, [ax, filterData, payTmDlg, payTmRows])

  // a line with no งวด yet opens on the current month
  const handleOpenMove = useCallback((row) => {
    setMoveDlg({ open: true, row, month: row.SalDte ? dayjs(row.SalDte) : dayjs().startOf("month") })
  }, [])

  const handleMoveConfirm = useCallback(() => {
    const { row, month } = moveDlg
    if (!row || !month) return
    ax.post("/branch/driver-payroll-v3/moveWageItemMonth", {
      WgItmId: row.WgItmId, ToSalDte: dayjs(month).format("YYYY-MM-DD"),
    }).then(value => {
      if (value.data) { setMoveDlg({ open: false, row: null, month: null }); getData(filterData) }
    })
  }, [ax, moveDlg, filterData, getData])

  // id 0 is the pinned sum row, not a wage line
  const isCellEditable = useCallback((params) => params.row.id !== 0 && !params.row.IsLocked, [])

  const columns = useMemo(
    () => flatColumns({ onRecalcRow: handleRecalcRow, onMovePayTm: handleOpenMove, onDeleteRow: handleDeleteRow }),
    [handleRecalcRow, handleOpenMove, handleDeleteRow])

  const handleCellClick = useCallback(({ field, row }) => {
    if (field === "ShowDetail" && row.JobOrdId != null) {
      selectedJobOrdId = row.JobOrdId
      setDailyJobDialogOpen(true)
    }
  }, [])
  useEffect(() => { getData(initFilter) }, [getData])

  return (
    <BoxFC height='100%' sx={{gap: 1}}>
      <BoxFR>
        <Box flex={1} />
        <TextField size="small" sx={{ width: 150 }} label="สถานะงวด" select
          value={filterData.Settle ?? ""}
          onChange={e => { const v = e.target.value === "" ? null : +e.target.value; const f = { ...filterData, Settle: v }; setFilterData(f)}}>
          <MenuItem value="">ทั้งหมด</MenuItem>
          <MenuItem value={2}>ยังไม่ลงค่าเที่ยว</MenuItem>
          <MenuItem value={0}>ยังไม่ยืนยันงวด</MenuItem>
          <MenuItem value={1}>ยืนยันงวดแล้ว</MenuItem>
        </TextField>
        <TextField size="small" sx={{ width: 130 }} label="สถานะหน้าที่" select
          value={filterData.Cmpt ?? ""}
          onChange={e => { const v = e.target.value === "" ? null : +e.target.value; const f = { ...filterData, Cmpt: v }; setFilterData(f)}}>
          <MenuItem value="">ไม่ระบุ</MenuItem>
          <MenuItem value={0}>ยังไม่เสร็จ</MenuItem>
          <MenuItem value={1}>เสร็จสิ้น</MenuItem>
        </TextField>
        <TextField size="small" sx={{ width: 100 }} label={jobOrderColNm.JobOrdId} value={filterData.JobOrdId ?? ""}
          onChange={e => setFilterData(o => ({ ...o, JobOrdId: e.target.value || null }))}
          onKeyUp={e => { if (e.key === "Enter") getData(filterData) }} />
        <TextField size="small" sx={{ width: 110 }} label={jobOrderColNm.JobNo} value={filterData.JobNo ?? ""}
          onChange={e => setFilterData(o => ({ ...o, JobNo: e.target.value || null }))}
          onKeyUp={e => { if (e.key === "Enter") getData(filterData) }} />
        <TextField size="small" sx={{ width: 120 }} label={jobOrderColNm.ContNo} value={filterData.ContNo ?? ""}
          onChange={e => setFilterData(o => ({ ...o, ContNo: e.target.value || null }))}
          onKeyUp={e => { if (e.key === "Enter") getData(filterData) }} />
        <ComboBox sx={{ width: 150 }} options={msData.jobCombo} label="งาน"
          selectedId={filterData.JobId} setSelectedId={(id) => setFilterData(o => ({ ...o, JobId: id }))} />
        <ComboBox sx={{ width: 150 }} options={drvCombo} label="พนักงานขับรถ"
          selectedId={filterData.DrvId} setSelectedId={(id) => setFilterData(o => ({ ...o, DrvId: id }))} />
        <DateTimeSelect sx={{ width: 180 }} label={jobOrderColNm.AptTm} start={filterData.AptTmSt} end={filterData.AptTmEn}
          setSelectDate={(st, en) => setFilterData(o => ({ ...o, AptTmSt: st, AptTmEn: en }))}
          getData={() => getData(filterData)} />
        <DateTimeSelect sx={{ width: 180 }} label='วันที่วิ่งงาน' start={filterData.StsTmSt} end={filterData.StsTmEn}
          setSelectDate={(st, en) => setFilterData(o => ({ ...o, StsTmSt: st, StsTmEn: en }))}
          getData={() => getData(filterData)} />
        <DesktopDatePicker disableMaskedInput openTo="month" views={["year", "month"]} label="งวดที่จ่าย"
          inputFormat="MM/YYYY" value={filterData.PayTm}
          onChange={(v) => { const f = { ...filterData, PayTm: v }; setFilterData(f); getData(f) }}
          renderInput={(params) => <TextField size="small" {...params} sx={{ width: 130 }} />} />
        <SearchButton onClick={() => getData(filterData)} />
        <ClearButton onClick={() => { const f = clearFilter(); setFilterData(f) }} />
      </BoxFR>
      <BoxFR>
        <ButtonGroup disabled={selectionModel.length === 0}>
          <Button sx={{ p: 1 }} variant="contained" onClick={handleCalcSelected} disabled={selectionModel.length === 0}>
            <CloudDownloadRounded sx={{ mr: 1 }} />ดึงข้อมูลในแถวที่เลือก{selectionModel.length ? ` (${selectionModel.length})` : ""}
          </Button>
          <Button sx={{ p: 1 }} variant="contained" color="success" onClick={handleOpenAddExp} disabled={selectionModel.length === 0}>
            <AddCircleRounded sx={{}} />
          </Button>
          <Button sx={{ p: 1 }} variant="contained" color="error" onClick={handleDeleteSelected} disabled={selectionModel.length === 0}>
            <DeleteForeverRounded />
          </Button>
        </ButtonGroup>
          <Button sx={{ p: 1 }} variant="contained" color="info" onClick={handleOpenSetPayTm} disabled={selectionModel.length === 0}>
            <EditCalendarRoundedIcon sx={{ mr: 1 }} />ตั้งงวดที่จ่าย{payTmRows.length ? ` (${payTmRows.length})` : ""}
          </Button>
      </BoxFR>
      <Box height='100%' sx={{
        mt: 1,
        ...sxSumColorClass,
        "& .group-odd": { bgcolor: grey[100] },
        "& .group-start": { borderTop: `1px solid ${grey[300]}` },
        "& .cell-need": { bgcolor: amber[50] },
        "& .cell-lock": { bgcolor: green[50] },
        "& .cell-deduct": { color: red[700] },
      }}>
        <DataGridCellExpand
          hideFooter
          checkboxSelection
          isRowSelectable={(p) => p.row.id !== 0 && !p.row.IsLocked}
          selectionModel={selectionModel}
          onSelectionModelChange={setSelectionModel}
          rows={modDataTable}
          columns={columns}
          isCellEditable={isCellEditable}
          onCellEditCommit={handleCellEditCommit}
          onCellClick={handleCellClick}
          experimentalFeatures={{ rowPinning: true }}
          pinnedRows={{ bottom: [modDataTable.find(row => row.id === 0)] }}
          getRowClassName={(p) => p.row.id === 0
            ? "total"
            : `${p.row.groupParity === 1 ? "group-odd" : "group-even"}${p.row.isGroupStart ? " group-start" : ""}`}
          getCellClassName={(params) => {
            if (params.field !== "Amnt") return ""
            if (params.row.IsLocked) return "cell-lock"                    // confirmed/paid
            if (params.row.ContStsId && params.row.Amnt == null) return "cell-need" // empty leg
            if (params.row.InOutTyp === "I") return "cell-deduct"          // deduction
            return ""
          }}
        />
      </Box>
      <DailyJobDialogV3
        dialogOpen={dailyJobDialogOpen}
        setDialogOpen={setDailyJobDialogOpen}
        selectedId={selectedJobOrdId}
      />
      <Dialog open={addExpDlg.open} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title"
        fullWidth maxWidth="xs">
        <DialogHeader handleDialogClose={() => setAddExpDlg(o => ({ ...o, open: false }))}
          title={`เพิ่มรายการ (เลือก ${selectionModel.length} แถว)`} onDeleteClick={null} />
        <DialogContent>
          <BoxFC pt={2}>
            <BoxFR justifyContent="center">
              <ComboBox sx={{ width: 220 }} options={msData.expCombo} label="รายการ"
                selectedId={addExpDlg.ExpId} setSelectedId={(id) => setAddExpDlg(o => ({ ...o, ExpId: id }))} />
              <TextField size="small" sx={{ width: 120 }} label="จำนวนเงิน" type="number"
                value={addExpDlg.Amnt} onChange={e => setAddExpDlg(o => ({ ...o, Amnt: e.target.value }))} />
            </BoxFR>
          </BoxFC>
        </DialogContent>
        <DialogFooter okText="เพิ่ม"
          handleDialogClose={() => setAddExpDlg(o => ({ ...o, open: false }))}
          handleDialogOk={handleAddExp} />
      </Dialog>
      <Dialog open={payTmDlg.open} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title"
        fullWidth maxWidth="xs">
        <DialogHeader handleDialogClose={() => setPayTmDlg(o => ({ ...o, open: false }))}
          title={`ตั้งงวดที่จ่าย (${payTmRows.length} รายการ)`} onDeleteClick={null} />
        <DialogContent>
          <BoxFC pt={2} alignItems="center">
            <DesktopDatePicker disableMaskedInput openTo="month" views={["year", "month"]} label="งวดที่จ่าย"
              inputFormat="MMMM YYYY" value={payTmDlg.month} onChange={(v) => setPayTmDlg(o => ({ ...o, month: v }))}
              renderInput={(params) => <TextField size="small" {...params} sx={{ width: 200 }} />} />
          </BoxFC>
        </DialogContent>
        <DialogFooter okText="ตั้งงวด"
          handleDialogClose={() => setPayTmDlg(o => ({ ...o, open: false }))}
          handleDialogOk={handleSetPayTm} />
      </Dialog>
      <Dialog open={moveDlg.open} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title"
        fullWidth maxWidth="xs">
        <DialogHeader handleDialogClose={() => setMoveDlg(o => ({ ...o, open: false }))}
          title={`ย้ายงวดที่จ่าย ${moveDlg.row ? `— ${moveDlg.row.ExpNm || ""}` : ""}`} onDeleteClick={null} />
        <DialogContent>
          <BoxFC pt={2} alignItems="center">
            <DesktopDatePicker disableMaskedInput openTo="month" views={["year", "month"]} label="งวดที่จ่าย"
              inputFormat="MMMM YYYY" value={moveDlg.month} onChange={(v) => setMoveDlg(o => ({ ...o, month: v }))}
              renderInput={(params) => <TextField size="small" {...params} sx={{ width: 200 }} />} />
          </BoxFC>
        </DialogContent>
        <DialogFooter okText="ย้าย"
          handleDialogClose={() => setMoveDlg(o => ({ ...o, open: false }))}
          handleDialogOk={handleMoveConfirm} />
      </Dialog>
    </BoxFC>
  )
}

export default TripPaymentBoard
